/**
 * S6 \u6b65\u6570\u4e0e\u5f97\u5206\u7ecf\u6d4e \u2014\u2014 \u5b9e\u73b0\u81ea design/gdd/systems/S6-moves-score-economy.md\u3002
 *
 * \u6570\u503c\u5168\u90e8\u6309 B \u6863\u5efa\u8bae\u503c\u5f00\u5de5\uff08\u7528\u6237\u88c1\u51b3\uff1a\u5148\u6309\u5efa\u8bae\u503c\uff0c\u8fb9\u73a9\u8fb9\u8c03\uff09\u3002
 * \u6240\u6709\u6570\u503c\u96c6\u4e2d\u5728 SCORE_CONST\uff0c\u4e0d\u8981\u6563\u843d\u5230\u4e1a\u52a1\u4ee3\u7801\u91cc\u3002
 */

import type { MatchGroup, PieceColor, SpecialType, Coord } from './types';
import type { CascadeStep } from './cascade';

/** B \u6863\u6570\u503c\uff08\u5747\u4e3a\u5f85\u8c03\u53c2\u6570\uff09 */
export const SCORE_CONST = {
  /** \u57fa\u7840\u6bcf\u683c\u5206 */
  basePerCell: 30,
  /** \u8d85\u51fa 3 \u683c\u7684\u6bcf\u683c\u989d\u5916\u5206 */
  lengthBonus: 40,

  /** \u7279\u6b8a\u5757\u751f\u6210\u5206\uff08\u6309\u4f18\u5148\u7ea7\uff09 */
  spawnScore: {
    THRUSTER: 60,
    METEOR_BURST: 100,
    BLACK_HOLE: 150,
    NEBULA_CORE: 200,
  } as Record<SpecialType, number>,

  /** \u5f15\u7206\u6bcf\u683c\u5206 */
  detonationPerCell: 30,
  detonationFixed: {
    THRUSTER: 50,
    METEOR_BURST: 80,
    BLACK_HOLE: 120,
    NEBULA_CORE: 160,
  } as Record<SpecialType, number>,

  /** \u8fde\u9501\u500d\u7387\u8f6f\u5c01\u9876\uff08D10 \u65b9\u6848 B\uff09 */
  slope: 0.5,
  multiplierCap: 3.0,

  /** \u5269\u4f59\u6b65\u6570\u5956\u52b1 */
  leftoverMoveScore: 30,
  leftoverCap: 10,

  /** \u661f\u7ebf\u7cfb\u6570 */
  starK2: 1.25,
  starK3: 1.5,
} as const;

/** S6 \u00a72.1\uff1a\u8fde\u9501\u500d\u7387\u3002min(1 + (level-1)\u00d7slope, cap) */
export function multiplier(level: number): number {
  return Math.min(1 + (level - 1) * SCORE_CONST.slope, SCORE_CONST.multiplierCap);
}

/** \u5355\u7ec4\u57fa\u7840\u5206\uff1a\u7ec4\u5927\u5c0f\u76f8\u5173\uff0c\u8d85\u51fa 3 \u683c\u90e8\u5206\u989d\u5916\u52a0\u5206 */
export function groupBaseScore(groupSize: number): number {
  return SCORE_CONST.basePerCell * groupSize
    + SCORE_CONST.lengthBonus * Math.max(0, groupSize - 3);
}

export interface MoveScoreInput {
  /** \u5404\u7ea7\u6d88\u9664\u7ec4 */
  groupsByLevel: { level: number; groups: MatchGroup[] }[];
  /** \u751f\u6210\u7684\u7279\u6b8a\u5757\uff08\u5e26\u751f\u6210\u65f6\u7684\u8fde\u9501\u7ea7\uff09 */
  spawns: { type: SpecialType; level: number }[];
  /** \u5f15\u7206\u4e8b\u4ef6\uff08\u5e26\u5f15\u7206\u65f6\u7684\u8fde\u9501\u7ea7\uff09 */
  detonations: { type: SpecialType; cellCount: number; level: number }[];
}

export interface MoveScoreResult {
  score: number;
  breakdown: {
    matchScore: number;
    spawnScore: number;
    detonationScore: number;
  };
}

/**
 * \u5355\u6b65\u5f97\u5206\u8ba1\u7b97\u3002
 * E8\uff1a\u53d6\u6574 Math.round \u4ec5\u5728\u672c\u6b21\u7ed3\u7b97\u672b\u6267\u884c\u4e00\u6b21\u3002
 */
export function computeMoveScore(input: MoveScoreInput): MoveScoreResult {
  let matchScore = 0;
  for (const { level, groups } of input.groupsByLevel) {
    const m = multiplier(level);
    for (const g of groups) matchScore += groupBaseScore(g.cells.length) * m;
  }

  let spawnScore = 0;
  for (const s of input.spawns) {
    spawnScore += SCORE_CONST.spawnScore[s.type] * multiplier(s.level);
  }

  let detonationScore = 0;
  for (const d of input.detonations) {
    const raw = SCORE_CONST.detonationPerCell * d.cellCount + SCORE_CONST.detonationFixed[d.type];
    detonationScore += raw * multiplier(d.level);
  }

  const total = matchScore + spawnScore + detonationScore;
  return {
    score: Math.round(total),
    breakdown: {
      matchScore: Math.round(matchScore),
      spawnScore: Math.round(spawnScore),
      detonationScore: Math.round(detonationScore),
    },
  };
}

/** \u5269\u4f59\u6b65\u6570\u5956\u52b1 */
export function leftoverScore(remainingMoves: number): number {
  return Math.min(remainingMoves, SCORE_CONST.leftoverCap) * SCORE_CONST.leftoverMoveScore;
}

/** \u7531 expectedScore \u63a8\u661f\u7ebf */
export function starLines(expectedScore: number): { star2: number; star3: number } {
  return {
    star2: Math.round(expectedScore * SCORE_CONST.starK2),
    star3: Math.round(expectedScore * SCORE_CONST.starK3),
  };
}

/** \u8bc4\u5b9a\u661f\u7ea7\uff081\u20133\uff09 */
export function evalStars(score: number, star2: number, star3: number): 1 | 2 | 3 {
  if (score >= star3) return 3;
  if (score >= star2) return 2;
  return 1;
}

export type { CascadeStep, PieceColor, Coord };
